import { useEffect, useRef } from "preact/hooks";
import { effect } from "@preact/signals";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";

import { buildStyle } from "../map/style";
import { atlasConfig, labEnabled } from "../map/atlas-config";
import { extraClassLabel, formatDate } from "../utils/format";
import { formatHistoricalDateRange } from "../utils/historicalDate";
import { workText } from "../utils/workTitle";
import {
  addConcertLayers,
  attachClusterHull,
  updateMarkers,
  interactivePointLayers,
  syncStableBand,
  CLUSTER_LAYER,
  SOURCE_ID,
  COARSE_POINTER,
} from "../map/markers";
import {
  clearPopupCityFilter,
  clearMapFocus,
  mapBounds,
  mapFocusRequest,
  mapCities,
  mapError,
  setCityFilter,
  theme,
  anchorISO,
  showExtras,
  listDirection,
  listDirectionExplicit,
  disabledSources,
  requestListScroll,
  listItems,
  listRefetching,
} from "../state/store";
import type { Concert, HistoricalConcert } from "../types";

const TILES_ASSET_URL =
  import.meta.env.VITE_TILES_URL ?? (import.meta.env.BASE_URL + "tiles/world.pmtiles");
const TILES_URL = "pmtiles://" + TILES_ASSET_URL;

// Register PMTiles protocol once
const protocol = new Protocol();
maplibregl.addProtocol("pmtiles", protocol.tile);

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  // The landing camera's zoom (hemisphere fit + 1) — the ONE "whole world" level. The
  // world reset returns here, not to minZoom: easing to minZoom made "zoom out" land
  // further out than the view a visitor starts at, so the globe visibly shrank the first
  // time they picked a filter.
  const homeZoomRef = useRef<number | null>(null);
  // True while a filter-triggered world reset is easing: its camera movement must not
  // count as the reader "touching" the viewport (below), or the reset itself would arm
  // the bbox filter it exists to clear.
  const worldEasingRef = useRef(false);
  const viewportTouchedRef = useRef(false);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    async function initMap() {
      mapError.value = null;

      try {
        const res = await fetch(TILES_ASSET_URL, {
          headers: { Range: "bytes=0-15" },
        });
        if (!(res.ok || res.status === 206)) {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (!cancelled) {
          mapError.value = import.meta.env.VITE_TILES_URL
            ? "Map failed to load. Please try refreshing the page."
            : "Basemap tiles are missing or unreadable. Run `./scripts/build-tiles.sh` so `web/public/tiles/world.pmtiles` exists, then reload the page. Details: " +
              message;
        }
        return;
      }

      if (cancelled || !containerRef.current) return;

      const style = buildStyle(
        theme.peek() === "dark" ? "dark" : "light",
        TILES_URL,
        atlasConfig.peek()
      );
      const map = new maplibregl.Map({
        container: containerRef.current,
        style,
        // The default view is now a GLOBE (projection: globe in both styles), which by
        // nature can't show every continent at once the way the old repeating-mercator
        // world strip did — in exchange, nothing is ever drawn twice. The initial fit
        // frames the Atlantic hemisphere (Europe + the Americas, where the data density
        // is), with the rest one drag away; bounds (not center+zoom) so the fit still
        // adapts to the container's aspect ratio on phones.
        bounds: [
          [-125, -42],
          [55, 68],
        ],
        fitBoundsOptions: { padding: 8 },
        // The world is 512·2^zoom px wide, so a pane narrower than 1024px can never
        // show all 360° at a fixed minZoom of 1 — phones topped out at ~145°. Derive
        // the floor from the pane width so zooming out to the whole world is always
        // reachable; wide panes keep the old floor of 1.
        minZoom: Math.min(1, Math.log2(containerRef.current.clientWidth / 512)),
        maxZoom: 12,
        attributionControl: false,
        // No cooperativeGestures: bare wheel zooms and one finger pans, like every map
        // site. The map is a bounded band of the document with grabbable page above and
        // below it, so nobody can get trapped — and the "Use Ctrl + scroll" nag (which
        // needed a MutationObserver workaround to mute) disappears with it.
      });

      // The bounds fit leaves the globe floating small in the pane; one level closer
      // fills it while the full sphere stays a single zoom-out away (where the list
      // drops its viewport filter entirely).
      map.setZoom(map.getZoom() + 1);
      homeZoomRef.current = map.getZoom();

      // Dev/Lab-only debugging handle (Playwright drives the real UI — see the Vite-HMR
      // separate-module-instance gotcha; this is the sanctioned back door).
      if (import.meta.env.DEV || labEnabled()) {
        (window as unknown as { __map: maplibregl.Map }).__map = map;
      }

      map.addControl(
        new maplibregl.AttributionControl({
          compact: true,
          customAttribution:
            'City data © <a href="https://www.geonames.org" target="_blank" rel="noopener">GeoNames</a> (CC BY 4.0)',
        }),
        "bottom-right"
      );
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

      map.on("error", (event) => {
        const message =
          event.error instanceof Error ? event.error.message : "Unknown map error";
        if (
          message.includes("pmtiles") ||
          message.includes("world.pmtiles") ||
          message.includes("Range")
        ) {
          mapError.value = import.meta.env.VITE_TILES_URL
            ? "Map failed to load. Please try refreshing the page."
            : "Basemap tiles failed to load. Confirm `web/public/tiles/world.pmtiles` exists and that your host supports HTTP Range Requests. Details: " +
              message;
        }
      });

      map.on("load", () => {
        addConcertLayers(map, theme.peek(), atlasConfig.peek());
        updateMarkers(map, mapCities.peek(), theme.peek(), atlasConfig.peek());
      });

      // Stable placement admits cities per integer zoom band (see markers.ts) — the
      // source data must be re-pushed when the band changes, but ONLY for intentional
      // zoom gestures. In globe projection, dragging across latitudes silently drifts
      // getZoom() (scale compensation), so zoom events during a drag — and during its
      // inertia, until moveend — must not move the band, or a long north-south pan
      // swaps the whole label set at "the same zoom" (the Aug-14 bug report).
      //
      // ⚠ A touch PINCH is both: touchPan runs concurrently with touchZoom, so every
      // pinch fires dragstart, and no zoom event follows moveend — a bare `panning`
      // guard left the band frozen at the load-time zoom forever on mobile (towns
      // never appeared). map.isZooming() is the discriminator: it reflects handler
      // zoomDelta (pinch/scroll/easing) and stays false for pure-pan globe drift.
      let panning = false;
      map.on("dragstart", () => {
        panning = true;
      });
      map.on("moveend", () => {
        panning = false;
      });
      map.on("zoom", () => {
        if (panning && !map.isZooming()) return;
        if (!syncStableBand(map)) return;
        updateMarkers(map, mapCities.peek(), theme.peek(), atlasConfig.peek());
      });

      // The list follows the viewport once the camera SETTLES (moveend), not during the
      // gesture. Publishing bounds mid-move (the pre-2026-09-07 100 ms throttle) re-ran the
      // list query on every wheel tick and inertia frame; each run aborted the previous one,
      // so a scroll-zoom spent ~2.5 s issuing requests that never completed and only then
      // started the one that did — the reader saw the old list sit unchanged for 5–10 s.
      // Markers don't depend on the viewport, so nothing else needed the live stream.
      //
      // No bbox until the reader moves the map: the initial camera (hemisphere fit +
      // one zoom) varies with the pane's aspect ratio, so publishing it would give
      // every visitor a unique landing /api/concerts URL — zero edge-cache hits — and
      // re-run the whole list query the moment the map settled. Until then the list is
      // deliberately worldwide (same meaning as the whole-globe view below). The flag
      // arms on drags immediately, and on ANY camera change once the initial camera has
      // settled ("idle"), so NavigationControl zooms and Show-on-map flights count too.
      viewportTouchedRef.current = false;
      const touchViewport = () => {
        if (worldEasingRef.current) return;
        viewportTouchedRef.current = true;
      };
      map.on("dragstart", touchViewport);
      map.once("idle", () => {
        map.on("movestart", touchViewport);
      });
      // Feedback from the first frame of a gesture: the list can't re-query until the
      // camera settles, but it can already SAY it's about to. Cleared by the query
      // lifecycle (a no-op re-query clears it too — see runUnifiedQuery), and here when
      // the camera settles on bounds that don't change the list's input.
      // Registered inside the same idle callback AFTER touchViewport, so on the very
      // first gesture the touched flag is already set when this runs (handlers fire in
      // registration order; an earlier registration saw `false` and never armed).
      map.once("idle", () => {
        map.on("movestart", () => {
          if (!viewportTouchedRef.current || worldEasingRef.current) return;
          if (listItems.peek().length > 0) listRefetching.value = true;
        });
      });
      const updateBounds = () => {
        if (!viewportTouchedRef.current || worldEasingRef.current) {
          listRefetching.value = false;
          return;
        }
        // Near full zoom-out the globe projection clamps getBounds() to the visible
        // hemisphere cap (~±79° of longitude), which silently drops far-side concerts
        // from the list while their markers stay visible on the globe. A whole-globe
        // view means "don't filter by viewport at all", so publish no bbox.
        if (map.getZoom() <= map.getMinZoom() + 0.5) {
          if (mapBounds.peek() === null) listRefetching.value = false; // no write → no re-query
          mapBounds.value = null;
          return;
        }
        const b = map.getBounds();
        mapBounds.value = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      };
      map.on("moveend", updateBounds);

      // One padded click handler for markers and clusters: hit-testing the exact pixel
      // made small-city dots near-untappable on phones, so the query box widens with the
      // pointer's coarseness and the nearest feature within it wins. City dots take
      // precedence over clusters — a popup beats a surprise zoom when both are in reach.
      const clickPad = COARSE_POINTER ? 12 : 4;
      map.on("click", (e) => {
        const box: [maplibregl.PointLike, maplibregl.PointLike] = [
          [e.point.x - clickPad, e.point.y - clickPad],
          [e.point.x + clickPad, e.point.y + clickPad],
        ];

        // Layer set depends on the marker mode (dots vs atlas symbols) — resolve the
        // ones that exist right now rather than assuming either mode's layout.
        const pointLayers = interactivePointLayers(map);
        const points = pointLayers.length
          ? map.queryRenderedFeatures(box, { layers: pointLayers })
          : [];
        const point = nearestFeature(map, points, e.point);
        if (point) {
          const props = point.properties!;
          const coords = (point.geometry as GeoJSON.Point).coordinates;
          showCityPopup(
            map,
            Number(props.id),
            [coords[0], coords[1]],
            String(props.name),
            String(props.country_name),
            Number(props.liveCount ?? props.count),
            Number(props.histCount ?? 0),
            props.upCount == null ? null : Number(props.upCount),
          );
          return;
        }

        const clusters = map.getLayer(CLUSTER_LAYER)
          ? map.queryRenderedFeatures(box, { layers: [CLUSTER_LAYER] })
          : [];
        const cluster = nearestFeature(map, clusters, e.point);
        if (cluster) {
          const clusterId = cluster.properties?.cluster_id;
          const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource;
          source.getClusterExpansionZoom(clusterId).then((zoom) => {
            const coords = (cluster.geometry as GeoJSON.Point).coordinates;
            map.flyTo({ center: [coords[0], coords[1]], zoom: zoom + 0.5 });
          });
        }
      });

      attachClusterHull(map);

      // Pointer cursor over any interactive marker. A single guarded handler instead of
      // per-layer delegated listeners: the layer set differs by marker mode, and
      // delegated listeners on a layer the current mode never created would query a
      // missing layer id.
      map.on("mousemove", (e) => {
        const layers = interactivePointLayers(map);
        if (map.getLayer(CLUSTER_LAYER)) layers.push(CLUSTER_LAYER);
        if (!layers.length) return;
        const features = map.queryRenderedFeatures(e.point, { layers });
        map.getCanvas().style.cursor = features.length ? "pointer" : "";
      });

      mapRef.current = map;
      if (import.meta.env.DEV) {
        (window as unknown as Record<string, unknown>).__map = map;
      }
      applyMapFocusRequest(map);
    }

    initMap();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      // Stale bounds must not keep filtering the list after the map is gone
      // (switching to Explore unmounts this component).
      mapBounds.value = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // React to unified map-city changes → update markers. `mapCities` already merges the live and
  // historical layers per their toggles, so this one effect covers both.
  // No isStyleLoaded() gate: it reports false whenever any source has pending
  // work (including our own setData), which silently dropped updates fired in
  // quick succession. updateMarkers no-ops while the source doesn't exist yet,
  // and the load/style.load handlers push the current data once it does.
  useEffect(() => {
    const dispose = effect(() => {
      const map = mapRef.current;
      const cities = mapCities.value; // unified live+archive source
      if (map) {
        // theme/config are peeked, not subscribed: the style effect below re-adds
        // layers and re-pushes data itself on a theme or atlas-config change.
        updateMarkers(map, cities, theme.peek() === "dark" ? "dark" : "light", atlasConfig.peek());
      }
    });
    return dispose;
  }, []);

  // Toggling a source off can invalidate an open popup (its counts / the filter it would
  // set may no longer apply) — close it rather than leave a stale one floating over
  // re-summed markers.
  useEffect(() => {
    const dispose = effect(() => {
      disabledSources.value;
      popupRef.current?.remove();
      popupRef.current = null;
    });
    return dispose;
  }, []);

  // Fly-to requests from the list ("Show on map").
  useEffect(() => {
    const dispose = effect(() => {
      mapFocusRequest.value?.seq;
      const map = mapRef.current;
      // flyTo/popups are safe regardless of style readiness — gating on
      // isStyleLoaded() dropped requests whenever tiles were still loading.
      if (!map) return;
      applyMapFocusRequest(map);
    });
    return dispose;
  }, []);

  // React to theme OR atlas-config changes → rebuild the style. One effect for both:
  // every lever flip is a full style rebuild anyway (layer sets differ between
  // configs), so the theme path and the Design Lab path share this code.
  useEffect(() => {
    const dispose = effect(() => {
      const t = theme.value === "dark" ? "dark" : "light";
      const cfg = atlasConfig.value;
      const map = mapRef.current;
      if (!map) return;

      const style = buildStyle(t, TILES_URL, cfg);
      // diff: false forces a full style reload so "style.load" fires; with
      // the default diffing, the event never fires and the concert layers
      // (absent from the new style spec) get removed and never re-added.
      map.setStyle(style, { diff: false });
      map.once("style.load", () => {
        addConcertLayers(map, t, cfg);
        updateMarkers(map, mapCities.peek(), t, cfg);
      });
    });
    return dispose;
  }, []);

  const error = mapError.value;

  return (
    <div class="map-shell">
      <div
        ref={containerRef}
        class="map-container"
        role="application"
        aria-label="Concert map"
        style={{ display: error ? "none" : "block" }}
      />
      {error && (
        <div class="map-error" role="alert">
          <div class="map-error-copy">{error}</div>
        </div>
      )}
    </div>
  );

  function applyMapFocusRequest(map: maplibregl.Map) {
    const req = mapFocusRequest.peek();
    if (!req) return;
    if (req.kind === "world") {
      // Restore the LANDING state, not just a wide camera: the home zoom (never minZoom —
      // see homeZoomRef), plus the untouched-viewport semantics that mean "list is
      // worldwide". Without the flag/bounds reset, this very ease would re-arm the bbox
      // filter and clip the fresh filter's list to the visible hemisphere.
      const homeZoom = homeZoomRef.current ?? map.getMinZoom();
      if (Math.abs(map.getZoom() - homeZoom) < 0.01) {
        // Already home (e.g. picking a second filter without touching the map): easing a
        // zero delta may never fire moveend, which would leave the suppression latched.
        viewportTouchedRef.current = false;
        mapBounds.value = null;
        return;
      }
      worldEasingRef.current = true;
      map.easeTo({ zoom: homeZoom, duration: 700 });
      map.once("moveend", () => {
        worldEasingRef.current = false;
        viewportTouchedRef.current = false;
        mapBounds.value = null;
      });
      return;
    }
    if (req.kind === "bounds") {
      // Padding matters beyond looks: the fitted viewport becomes the list's bbox filter
      // (see the touchViewport/updateBounds pair above), so the framed region has to sit
      // *inside* the viewport, not flush against its edges, or the camera would clip the
      // very rows the city/country filter selected.
      const [w, s, e, n] = req.bbox;
      map.fitBounds(
        [
          [w, s],
          [e, n],
        ],
        { padding: req.padding ?? 48, maxZoom: req.maxZoom, duration: 700, essential: true },
      );
      return;
    }
    if (req.kind === "concert" && req.item) {
      map.flyTo({ center: [req.lon, req.lat], zoom: Math.max(map.getZoom(), 8), essential: true });
      if (req.item.kind === "live") {
        showConcertPopup(map, req.item.concert, [req.lon, req.lat]);
      } else {
        showHistoricalConcertPopup(map, req.item.concert, [req.lon, req.lat]);
      }
      return;
    }
    const city = mapCities.peek().find((item) => item.id === req.cityId);
    if (!city) return;
    map.flyTo({ center: [req.lon, req.lat], zoom: Math.max(map.getZoom(), 6.5), essential: true });
    showCityPopup(map, city.id, [req.lon, req.lat], city.name, city.country_name,
      city.liveCount ?? city.count, city.histCount ?? 0, city.upCount ?? null);
  }

  /** Unified city popup: quotes the same Upcoming/Past split the list shows (upcoming =
   *  live rows on/after the anchor; past = the rest plus the archive — live-vs-archive is
   *  not a distinction the reader sees here). Clicking a count narrows the list to this
   *  city, flips it to the matching reading direction, and scrolls down to it — the
   *  popup sits on the map, well above the list it just rewrote. */
  function showCityPopup(
    map: maplibregl.Map,
    cityId: number,
    coords: [number, number],
    name: string,
    countryName: string,
    liveCount: number,
    histCount: number,
    upCount: number | null,
  ) {
    const content = document.createElement("div");
    content.className = "city-popup";

    const title = document.createElement("strong");
    title.textContent = name;
    content.appendChild(title);
    content.appendChild(document.createElement("br"));

    const country = document.createElement("span");
    country.textContent = countryName;
    content.appendChild(country);

    const addCountLink = (label: string, onClick: () => void) => {
      content.appendChild(document.createElement("br"));
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "popup-count popup-count-link";
      btn.textContent = label;
      btn.addEventListener("click", onClick);
      content.appendChild(btn);
    };

    if (anchorISO.peek() !== null && upCount !== null) {
      const pastCount = liveCount - upCount + histCount;
      if (upCount > 0) {
        // With the toggle on, these are not all concerts.
        const noun = showExtras.peek() ? "event" : "concert";
        addCountLink(`${upCount} upcoming ${noun}${upCount !== 1 ? "s" : ""}`, () => {
          listDirection.value = "forward";
          listDirectionExplicit.value = true;
          setCityFilter(cityId, "popup");
          requestListScroll();
        });
      }
      if (pastCount > 0) {
        addCountLink(`${pastCount} past event${pastCount !== 1 ? "s" : ""}`, () => {
          listDirection.value = "backward";
          listDirectionExplicit.value = true;
          setCityFilter(cityId, "popup");
          requestListScroll();
        });
      }
    } else {
      // Window excludes today (one chronological list, no directions) or the split is
      // still unknown (cold start on baked all-time counts): claim only a total —
      // never "upcoming", which these numbers can't back up.
      const total = liveCount + histCount;
      if (total > 0) {
        addCountLink(`${total} event${total !== 1 ? "s" : ""}`, () => {
          setCityFilter(cityId, "popup");
          requestListScroll();
        });
      }
    }

    openPopup(map, coords, content, cityId);
  }

  function showConcertPopup(
    map: maplibregl.Map,
    concert: Concert,
    coords: [number, number],
  ) {
    const lines = [
      concert.concert_title
        ? `<strong>${escapeHtml(concert.concert_title)}</strong>`
        : `<strong>${escapeHtml(concert.org_name)}</strong>`,
      concert.concert_title
        ? `<span class="popup-count">${escapeHtml(concert.org_name)}</span>`
        : null,
      concert.extra_class
        ? `<span class="popup-count">${escapeHtml(extraClassLabel(concert.extra_class))}</span>`
        : null,
      escapeHtml(formatDate(concert.date_start)),
      escapeHtml(
        concert.venue_name
          ? `${concert.venue_name}, ${concert.city_name}`
          : concert.city_name
      ),
    ].filter((line): line is string => Boolean(line));
    if (concert.program.length > 0) {
      const preview = concert.program
        .slice(0, 2)
        .map((entry) => {
          const composers = entry.composers.map((composer) => composer.name).join(", ");
          const arrangers = entry.arrangers.map((arranger) => arranger.name).join(", ");
          let line = entry.work
            ? (composers ? `${composers}: ${entry.work}` : entry.work)
            : composers;
          if (arrangers) {
            line = line ? `${line} (arr. ${arrangers})` : `arr. ${arrangers}`;
          }
          return line;
        })
        .filter((line) => line.length > 0)
        .join(" | ");
      lines.push(`<span class="popup-count">${escapeHtml(preview)}</span>`);
    }
    const content = document.createElement("div");
    content.className = "concert-popup";
    content.innerHTML = lines.join("<br/>");
    openPopup(map, coords, content, concert.city_id);
  }

  /** Archive mode's popup: same shape as `showConcertPopup`, but the date line renders the
   *  honest EDTF precision (never a fabricated instant) and — where the record is a memoir
   *  mention rather than an archive row — a one-line quote preview stands in for the program
   *  preview, since a "winter 1812/13" mention may have no structured program at all. */
  function showHistoricalConcertPopup(
    map: maplibregl.Map,
    concert: HistoricalConcert,
    coords: [number, number],
  ) {
    const lines = [
      `<strong>${escapeHtml(concert.title || "Untitled")}</strong>`,
      escapeHtml(formatHistoricalDateRange(concert)),
      escapeHtml(
        concert.venue_name ? `${concert.venue_name}, ${concert.city_name}` : concert.city_name
      ),
    ];
    if (concert.program.length > 0) {
      const preview = concert.program
        .slice(0, 2)
        .map((entry) => {
          const composers = entry.composers.map((c) => c.name).join(", ");
          const work = workText(entry);
          return composers ? `${composers}: ${work}` : work;
        })
        .filter((line) => line.length > 0)
        .join(" | ");
      lines.push(`<span class="popup-count">${escapeHtml(preview)}</span>`);
    } else if (concert.quote) {
      lines.push(`<span class="popup-count">&ldquo;${escapeHtml(truncate(concert.quote, 90))}&rdquo;</span>`);
    }
    const content = document.createElement("div");
    content.className = "concert-popup";
    content.innerHTML = lines.join("<br/>");
    openPopup(map, coords, content, concert.city_id);
  }

  function openPopup(
    map: maplibregl.Map,
    coords: [number, number],
    content: string | Node,
    cityId: number,
  ) {
    if (popupRef.current) popupRef.current.remove();
    const popup = new maplibregl.Popup({ offset: 12, closeButton: true }).setLngLat([
      coords[0],
      coords[1],
    ]);
    if (typeof content === "string") {
      popup.setHTML(content);
    } else {
      popup.setDOMContent(content);
    }
    popup.addTo(map);
    popup.on("close", () => {
      const req = mapFocusRequest.peek();
      // Narrow positively on the kinds that carry a cityId — "not world" stopped being
      // enough once region-framing requests joined the union.
      const targetsThisCity =
        req !== null && (req.kind === "city" || req.kind === "concert") && req.cityId === cityId;
      if (targetsThisCity) clearMapFocus();
      clearPopupCityFilter(cityId);
    });
    popupRef.current = popup;
  }
}

/** Of the features a padded click box caught, the one whose anchor is nearest the
 *  actual click/tap point on screen. */
function nearestFeature(
  map: maplibregl.Map,
  features: maplibregl.MapGeoJSONFeature[],
  point: { x: number; y: number },
): maplibregl.MapGeoJSONFeature | null {
  let best: maplibregl.MapGeoJSONFeature | null = null;
  let bestD = Infinity;
  for (const f of features) {
    const coords = (f.geometry as GeoJSON.Point).coordinates;
    const p = map.project([coords[0], coords[1]]);
    const d = (p.x - point.x) ** 2 + (p.y - point.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = f;
    }
  }
  return best;
}

function truncate(text: string, maxLen: number): string {
  return text.length <= maxLen ? text : text.slice(0, maxLen - 1) + "…";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
